import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { HAZARD_SVG } from './hazardIcon';
import { hazardMarkup } from './hazardMarkup';
import { formatVertexLabel } from '../../../utils/gdacs/gdacsTime';
import { makeTrackFrame, trackLngPairs, footprintIsShifted } from '../../../utils/gdacs/trackFrame';
import { fitPaddings, layoutKey } from '../../../utils/mapInsets';
import { playStyles, CASING_ALPHA, RADIUS } from '../../../utils/gdacs/playStyle';
import { iconSizePx, quantisePx } from '../../../utils/gdacs/stormIconSize';
import { shouldGlide, setSvgMotion, visibleSegments, rectInside, isClear } from '../../../utils/gdacs/mapMotion';

// Wind zones and the track line sit above rain tiles / routes (<= 400) and below region pins (600); the storm icon, hazard markers (695), class discs and time chips sit above the pins; popups are 700.
const PANES = {
  fill: ['hazard-fill', 450],
  line: ['hazard-line', 460],
  storm: ['storm-icon', 694],
  disc: ['hazard-disc', 696],     // class discs and time chips sit above the region pins, the storm icon and the hazard markers
  label: ['hazard-label', 697],
};

// Wind zones: an orange family ordered light -> dark by strength (>=60 amber, >=90 orange, >=120 burnt orange).
// Fills are plain translucent polygons that stack (overlaps read darker); crisp edges keep every zone readable
// on yellow, orange, green and blue-purple rasters. The edges flip lighter on the dark theme.
const WIND_BANDS = [60, 90, 120];
const WIND_COLORS = { 60: '#f59e0b', 90: '#ea580c', 120: '#9a3412' };
const WIND_EDGE = {
  light: { 60: '#b45309', 90: '#9a3412', 120: '#5c1d0a' },
  dark: { 60: '#fcd34d', 90: '#fdba74', 120: '#fca5a5' },
};
const SWATH_STYLE = { weight: 1.25, opacity: 0.4, dashArray: '2 5' };
const CONE_STYLE = {
  light: { fillColor: '#82c9ed', fillOpacity: 0.18, color: '#1683bc', weight: 2.5, opacity: 0.95 },
  dark: { fillColor: '#82c9ed', fillOpacity: 0.22, color: '#79ceff', weight: 2.5, opacity: 0.95 },
};
// Track: bold green line over a persistent white casing (never faded), past and forecast alike.
const TRACK_COLOR = '#16b84e';
const TRACK_WEIGHT = 4;
const CASING_WEIGHT = 8;
const CASING_COLOR = '#ffffff';
const DOT_STYLE = { color: '#ffffff', fillColor: '#64748b', weight: 1.5 };
const CLASS_COLORS = { HU: '#b91c3a', TS: '#b4690e', TD: '#887522', UNK: '#475569' };
const GLYPH_RED = '#dc243d';
const GLYPH_CASING_PX = 2;
const HISTORICAL_GLYPH_PX = 14;
const PLAYHEAD_PX = 32;
const FLOOD_COLOR = '#0369A1';
const QUAKE_RAMP = ['#fde68a', '#fbbf24', '#d97706', '#92400e', '#78350f'];
const CHIP_GAP = 13;          // px between a disc centre and its chip
const CHIP_PLACEMENTS = ['r', 'l', 't', 'b', 'tr', 'br', 'tl', 'bl'];

const toLL = (ring) => ring.map(([lng, lat]) => [lat, lng]);
const preparedLatLngs = (prepared) => prepared.polys.map((p) => [toLL(p.outer), ...p.holes.map(toLL)]);
const reducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Fits the selected hazard into the free rectangle of the map (outside the strip, legends and the storm card).
 * Runs once per selection (and per repeat click) and again only when the measured layout changes.
 */
export function FitHazardOnReady({ bounds, ready, selectedId, fitNonce, insets }) {
  const map = useMap();
  const key = layoutKey(insets);
  useEffect(() => {
    if (!ready || !bounds || selectedId == null || !insets.width) return undefined;
    // Make Leaflet re-measure its container (synchronous), then fit into the free rectangle.
    const run = () => {
      map.invalidateSize({ animate: false, pan: false });
      map.stop();
      map.fitBounds(bounds, fitPaddings(insets, map.getSize()));
    };
    // Let the layout settle first. map.stop() does not cancel a running zoom animation (a fit started during one
    // is discarded), so in that case the fit waits for the zoom to finish.
    let raf2 = 0;
    const afterZoom = () => { raf2 = requestAnimationFrame(run); };   // let the finished zoom's own events settle first
    const raf = requestAnimationFrame(() => {
      if (map._animatingZoom) map.once('zoomend', afterZoom);
      else run();
    });
    return () => {
      cancelAnimationFrame(raf); cancelAnimationFrame(raf2);
      map.off('zoomend', afterZoom);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, bounds, ready, selectedId, fitNonce, key]);   // refit only on selection, a repeat click, or a layout change
  return null;
}

const escapeHtml = (v) => String(v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// The cyclone glyph with the map's red and a white casing of `casingPx` screen pixels at `size` px.
// `still` drops the pulse ring and the rotation (historical markers do not animate).
function stormGlyphMarkup(size, { still = false, casingPx = GLYPH_CASING_PX } = {}) {
  let svg = HAZARD_SVG.cyclone.replaceAll('#E11D2E', GLYPH_RED)
    .replace(/stroke-width="3\.4"/, `stroke-width="${((2 * casingPx * 48) / size).toFixed(2)}"`);
  if (still) svg = svg.replace(/<circle[\s\S]*?<\/circle>/, '').replace(/<animateTransform[^>]*\/>/, '');
  return hazardMarkup(svg, { size });
}

const edgeStyle = (band, dark, w) => ({
  fill: false, color: WIND_EDGE[dark ? 'dark' : 'light'][band], weight: w.weight, opacity: w.opacity, lineJoin: 'round',
});
const pickPlacement = (key, w, h) => {
  const g = CHIP_GAP;
  switch (key) {
    case 'r': return [g, -h / 2];
    case 'l': return [-g - w, -h / 2];
    case 't': return [-w / 2, -g - h];
    case 'b': return [-w / 2, g];
    case 'tr': return [g - 4, -g - h + 4];
    case 'br': return [g - 4, g - 4];
    case 'tl': return [-g + 4 - w, -g - h + 4];
    default: return [-g + 4 - w, g - 4];
  }
};

/**
 * Draws one hazard footprint with plain Leaflet layers. The static chart is rebuilt from `parsed`
 * only; playback ticks only restyle existing paths, move the storm marker and scale its glyph (no polygon
 * rebuild, no divIcon recreation). Wind zones are simple per-set polygons whose translucent fills stack.
 */
const HazardGeometryLayer = ({
  parsed,
  clockState,
  clockMs,
  isPlaying,
  playSpeed,
  isDark,
  isCurrent = true,
  scrubRevision = 0,
  labels,
}) => {
  const map = useMap();
  const groupsRef = useRef(null);
  const markerRef = useRef(null);
  const windPathsRef = useRef(new Map());   // setIndex -> [{ fill, edge, band }]
  const segPathsRef = useRef([]);           // [{ path, index }] in parsed.track.segments order
  const dotsRef = useRef([]);               // [{ dot, index }] historical dots
  const pinsRef = useRef([]);               // [{ marker, index }] forecast discs and historical glyphs
  const chipsRef = useRef([]);              // [{ marker, span, index, timeMs, lat, lng }]
  const lastClockMsRef = useRef(null);
  const lastScrubRef = useRef(scrubRevision);
  const toFrameRef = useRef((lng) => lng);
  const appliedRef = useRef(new Map());
  const activeSetRef = useRef(null);
  const latestRef = useRef(-2);
  const sizeRef = useRef(PLAYHEAD_PX);
  const isPlayingRef = useRef(isPlaying);
  const playSpeedRef = useRef(playSpeed);
  const clockStateRef = useRef(clockState);
  const parsedRef = useRef(parsed);
  parsedRef.current = parsed;
  isPlayingRef.current = isPlaying;
  playSpeedRef.current = playSpeed;
  clockStateRef.current = clockState;

  // Chips go in clear space beside the track: the active chip first, then one per UTC day, then the rest.
  // A chip takes the first placement that clears the other chips, every disc/dot and the track line; if none
  // does it is suppressed (the active chip falls back to the first placement).
  const applyLabelVisibility = (hideAll = false) => {
    const chips = chipsRef.current;
    if (!chips.length) return;
    if (hideAll || !parsedRef.current) {
      chips.forEach((c) => { c.span.style.display = 'none'; });
      return;
    }
    const pt = (lat, lng) => map.latLngToContainerPoint([lat, lng]);
    const size = map.getSize();
    const view = [0, 0, size.x, size.y];
    const obstacles = [];   // [x, y, w, h]: track vertices and the status pins of the marker pane
    const tr = parsedRef.current.track;
    tr.vertices.forEach((v) => {
      const p = pt(v.lat, toFrameRef.current(v.lng));
      const r = v.phase === 'forecast' ? RADIUS.latest + 2 : 6;
      obstacles.push([p.x - r, p.y - r, 2 * r, 2 * r]);
    });
    const box = map.getContainer().getBoundingClientRect();
    map.getPane('markerPane')?.querySelectorAll('.leaflet-marker-icon').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) obstacles.push([r.left - box.left, r.top - box.top, r.width, r.height]);
    });
    // Track segments are tested against candidate rectangles directly (no sampling); only those that touch
    // the viewport can ever matter.
    const segments = visibleSegments(tr.segments.map((s) => {
      const a = pt(s.from[1], toFrameRef.current(s.from[0]));
      const b = pt(s.to[1], toFrameRef.current(s.to[0]));
      return [a.x, a.y, b.x, b.y];
    }), [-4, -4, size.x + 8, size.y + 8]);
    const seenDay = new Set();
    const ranked = chips.map((c) => {
      const day = Math.floor(c.timeMs / 86_400_000);
      const daily = !seenDay.has(day);
      seenDay.add(day);
      return { c, rank: c.index === latestRef.current ? 0 : daily ? 1 : 2 };
    }).sort((x, y) => x.rank - y.rank || x.c.timeMs - y.c.timeMs);
    const placed = [];
    ranked.forEach(({ c, rank }) => {
      const p = pt(c.lat, toFrameRef.current(c.lng));
      c.span.style.display = '';
      const w = c.span.offsetWidth;
      const h = c.span.offsetHeight;
      let chosen = null;
      const candidates = CHIP_PLACEMENTS.map((key) => {
        const [dx, dy] = pickPlacement(key, w, h);
        return { dx, dy, rect: [p.x + dx, p.y + dy, w, h] };
      }).filter((cand) => rectInside(cand.rect, view));
      chosen = candidates.find((cand) => isClear(cand.rect, { placed, obstacles, segments })) || null;
      // The active chip is never dropped: take the first placement that is at least inside the viewport.
      if (!chosen && rank === 0) chosen = candidates[0] || null;
      if (!chosen) {
        c.span.style.display = 'none';
        return;
      }
      c.span.style.transform = `translate(${chosen.dx}px, ${chosen.dy}px)`;
      placed.push(chosen.rect);
    });
  };

  // Glide: the outer marker eases to each new position and the inner wrapper eases its scale, both linear over
  // the play speed. Any other change of the playhead (scrub, wrap, pause, speed, zoom, reduced motion) snaps.
  const applyGlide = (on) => {
    const el = markerRef.current?.getElement();
    if (!el) return;
    el.classList.toggle('storm-icon--glide', on);
    el.style.transitionDuration = on ? `${playSpeedRef.current}ms` : '';
    const inner = el.querySelector('.storm-icon__inner');
    if (inner) inner.style.transition = on ? `transform ${playSpeedRef.current}ms linear` : 'none';
  };
  // The moving glyph carries SVG <animate>/<animateTransform>; CSS cannot stop those.
  const syncGlyphMotion = () => {
    const svg = markerRef.current?.getElement()?.querySelector('svg');
    setSvgMotion(svg, reducedMotion());
  };
  const applySize = (px) => {
    const target = quantisePx(px);
    sizeRef.current = target;
    const inner = markerRef.current?.getElement()?.querySelector('.storm-icon__inner');
    if (inner) inner.style.transform = `scale(${(target / PLAYHEAD_PX).toFixed(4)})`;
  };

  // Mount: panes, renderers, empty groups, zoom listeners. Everything is torn down on unmount
  // (the home map remounts on a theme change).
  useEffect(() => {
    Object.values(PANES).forEach(([name, z]) => {
      if (!map.getPane(name)) {
        const p = map.createPane(name);
        p.style.zIndex = z;
      }
      map.getPane(name).style.pointerEvents = 'none';
    });
    const fillRenderer = L.svg({ pane: PANES.fill[0] });
    const lineRenderer = L.svg({ pane: PANES.line[0] });
    const make = () => L.layerGroup().addTo(map);
    groupsRef.current = {
      fillRenderer,
      lineRenderer,
      cone: make(),
      fills: make(),
      lines: make(),
      pins: make(),
      chips: make(),
      storm: make(),
    };
    const onZoomStart = () => {
      applyGlide(false);
      applyLabelVisibility(true);
    };
    const onZoomEnd = () => {
      if (isPlayingRef.current && !reducedMotion()) applyGlide(true);
    };
    const onMoveEnd = () => applyLabelVisibility();   // pan, zoom and fit all end with moveend
    map.on('zoomstart', onZoomStart);
    map.on('zoomend', onZoomEnd);
    map.on('moveend', onMoveEnd);
    // Reduced motion can change while the page is open: pause or resume the glyph animations and drop any glide.
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const onMotionPref = () => {
      syncGlyphMotion();
      if (reducedMotion()) applyGlide(false);
    };
    mq?.addEventListener?.('change', onMotionPref);
    return () => {
      map.off('zoomstart', onZoomStart);
      map.off('zoomend', onZoomEnd);
      map.off('moveend', onMoveEnd);
      mq?.removeEventListener?.('change', onMotionPref);
      const g = groupsRef.current;
      if (g) {
        ['cone', 'fills', 'lines', 'pins', 'chips', 'storm'].forEach((k) => {
          g[k].clearLayers();
          map.removeLayer(g[k]);
        });
        map.removeLayer(g.fillRenderer);
        map.removeLayer(g.lineRenderer);
      }
      groupsRef.current = null;
      markerRef.current = null;
      windPathsRef.current = new Map();
      segPathsRef.current = [];
      dotsRef.current = [];
      pinsRef.current = [];
      chipsRef.current = [];
      appliedRef.current = new Map();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Static chart: rebuilt from the parsed footprint, never from the raw feature collection.
  useEffect(() => {
    const g = groupsRef.current;
    if (!g) return;
    ['cone', 'fills', 'lines', 'pins', 'chips', 'storm'].forEach((k) => g[k].clearLayers());
    windPathsRef.current = new Map();
    segPathsRef.current = [];
    dotsRef.current = [];
    pinsRef.current = [];
    chipsRef.current = [];
    appliedRef.current = new Map();
    activeSetRef.current = null;
    latestRef.current = -2;
    markerRef.current = null;
    lastClockMsRef.current = null;
    if (!parsed) {
      // Nothing selected: drop the (empty) renderers too; Leaflet re-adds a renderer with its first path.
      map.removeLayer(g.fillRenderer);
      map.removeLayer(g.lineRenderer);
      return;
    }

    const fillOpts = { pane: PANES.fill[0], renderer: g.fillRenderer, interactive: false, className: 'hazard-fade' };
    const lineOpts = { pane: PANES.line[0], renderer: g.lineRenderer, interactive: false, className: 'hazard-fade' };
    const poly = (prepared, opts) => L.polygon(preparedLatLngs(prepared), opts);
    const tone = isDark ? 'dark' : 'light';

    if (parsed.cone) {
      poly(parsed.cone, { ...fillOpts, ...CONE_STYLE[tone] }).addTo(g.cone);
    }

    if (parsed.flood?.affected) {
      poly(parsed.flood.affected, {
        ...fillOpts, color: FLOOD_COLOR, weight: 1, fillColor: FLOOD_COLOR, fillOpacity: 0.2,
      }).addTo(g.fills);
    }

    if (parsed.quake) {
      const ascending = [...parsed.quake.bands].reverse();
      ascending.forEach((band, i) => {
        const color = QUAKE_RAMP[Math.min(QUAKE_RAMP.length - 1, Math.floor((i * QUAKE_RAMP.length) / ascending.length))];
        poly(band.prepared, {
          ...fillOpts, color, weight: 1, fillColor: color, fillOpacity: 0.25,
        }).addTo(g.fills);
      });
      if (parsed.quake.circle) {
        poly(parsed.quake.circle, {
          ...lineOpts, fill: false, color: '#6b7280', weight: 1, dashArray: '6 6',
        }).addTo(g.lines);
      }
    }

    // Whole-path swaths: edge only.
    WIND_BANDS.forEach((band) => {
      if (!parsed.windSwath[band]) return;
      poly(parsed.windSwath[band], {
        ...lineOpts, fill: false, color: WIND_EDGE[tone][band], ...SWATH_STYLE,
      }).addTo(g.lines);
    });

    // Wind zones at every timed position: fills first (weaker bands underneath), then all edges on top.
    const fillsBySet = new Map();
    WIND_BANDS.forEach((band) => {
      parsed.windByTime.forEach((entry, idx) => {
        if (!entry.sets[band]) return;
        const p = poly(entry.sets[band], {
          ...fillOpts, stroke: false, fillColor: WIND_COLORS[band], fillOpacity: 0.3,
        }).addTo(g.fills);
        if (!fillsBySet.has(idx)) fillsBySet.set(idx, []);
        fillsBySet.get(idx).push({ fill: p, band });
      });
    });
    parsed.windByTime.forEach((entry, idx) => {
      const list = (fillsBySet.get(idx) || []).map(({ fill, band }) => ({
        fill,
        band,
        edge: poly(entry.sets[band], { ...fillOpts, ...edgeStyle(band, isDark, { weight: 1.5, opacity: 0.8 }) }).addTo(g.fills),
      }));
      windPathsRef.current.set(idx, list);
    });

    const track = parsed.track;
    const toFrame = makeTrackFrame(trackLngPairs(track), footprintIsShifted(parsed));
    toFrameRef.current = toFrame;
    const lineStyle = { ...lineOpts, color: TRACK_COLOR, weight: TRACK_WEIGHT, opacity: 1, lineCap: 'round', lineJoin: 'round' };
    const casingStyle = { ...lineOpts, className: undefined, color: CASING_COLOR, weight: CASING_WEIGHT, opacity: CASING_ALPHA, lineCap: 'round', lineJoin: 'round' };
    if (track.trackIncomplete) {
      const raw = track.rawSegments.map((s) => [[s.from[1], toFrame(s.from[0])], [s.to[1], toFrame(s.to[0])]]);
      raw.forEach((ll) => L.polyline(ll, casingStyle).addTo(g.lines));
      raw.forEach((ll) => L.polyline(ll, lineStyle).addTo(g.lines));
    } else {
      // All casings first, so no casing ever covers a neighbouring segment's line.
      const segLL = track.segments.map((s) => [[s.from[1], toFrame(s.from[0])], [s.to[1], toFrame(s.to[0])]]);
      segLL.forEach((ll) => L.polyline(ll, casingStyle).addTo(g.lines));
      track.segments.forEach((s, index) => {
        const path = L.polyline(segLL[index], lineStyle).addTo(g.lines);
        segPathsRef.current.push({ path, index });
      });
      let seq = 0;
      const marker = (v, html, cls) => L.marker([v.lat, toFrame(v.lng)], {
        icon: L.divIcon({ className: cls, html, iconSize: [0, 0], iconAnchor: [0, 0] }),
        interactive: false, keyboard: false, pane: PANES.disc[0], zIndexOffset: 1000,
      }).addTo(g.pins);
      track.vertices.forEach((v, index) => {
        if (v.phase === 'past') {
          if (v.cls === 'HU') {
            const m = marker(v, `<span class="hazard-glyph" style="width:${HISTORICAL_GLYPH_PX}px;height:${HISTORICAL_GLYPH_PX}px">${stormGlyphMarkup(HISTORICAL_GLYPH_PX, { still: true })}</span>`, 'hazard-pin-icon');
            pinsRef.current.push({ marker: m, index });
          } else {
            const dot = L.circleMarker([v.lat, toFrame(v.lng)], {
              ...lineOpts, radius: RADIUS.past, ...DOT_STYLE, fillOpacity: 1,
            }).addTo(g.lines);
            dotsRef.current.push({ dot, index });
          }
          return;
        }
        seq += 1;
        const supplied = v.cls !== 'UNK' ? labels?.shortClasses?.[v.cls] : '';
        const text = supplied || String(seq);
        const m = marker(v, `<span class="hazard-disc" style="background:${CLASS_COLORS[v.cls] || CLASS_COLORS.UNK}">${escapeHtml(text)}</span>`, 'hazard-pin-icon');
        pinsRef.current.push({ marker: m, index });
        if (v.timeMs != null) {
          const chip = L.marker([v.lat, toFrame(v.lng)], {
            icon: L.divIcon({
              className: 'hazard-chip-icon',
              html: `<span class="hazard-vertex-label">${escapeHtml(formatVertexLabel(v.timeMs))}</span>`,
              iconSize: [0, 0],
              iconAnchor: [0, 0],
            }),
            interactive: false, keyboard: false, pane: PANES.label[0], zIndexOffset: 1000,
          }).addTo(g.chips);
          chipsRef.current.push({
            span: chip.getElement().firstChild, index, timeMs: v.timeMs, lat: v.lat, lng: toFrame(v.lng),
          });
        }
      });
    }

    if (track.animatable && isCurrent) {
      const first = iconSizePx(parsed, clockMs) ?? PLAYHEAD_PX;
      const icon = L.divIcon({
        className: 'storm-icon',
        html: `<div class="storm-icon__inner" style="width:${PLAYHEAD_PX}px;height:${PLAYHEAD_PX}px;transform:scale(${(quantisePx(first) / PLAYHEAD_PX).toFixed(4)})">${stormGlyphMarkup(PLAYHEAD_PX)}</div>`,
        iconSize: [PLAYHEAD_PX, PLAYHEAD_PX],
        iconAnchor: [PLAYHEAD_PX / 2, PLAYHEAD_PX / 2],
      });
      sizeRef.current = quantisePx(first);
      const pos = clockStateRef.current?.position || parsed.centroid;
      markerRef.current = L.marker([pos[1], toFrame(pos[0])], {
        icon, interactive: false, keyboard: false, pane: PANES.storm[0],
      }).addTo(g.storm);
      syncGlyphMotion();
    }
    applyLabelVisibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed, isDark, labels, isCurrent, map]);

  // Playback: restyle the existing paths (day fade, latest disc, active zone) and move + scale the storm.
  // Nothing is rebuilt here; a path is only touched when its style changed.
  useEffect(() => {
    if (!parsed) return;
    if (parsed.eventType === 'TC') {
      const st = playStyles(parsed, clockState, clockMs);
      const applied = appliedRef.current;
      const restyle = (path, style, key) => {
        if (applied.get(path) === key) return;
        applied.set(path, key);
        path.setStyle(style);
      };
      segPathsRef.current.forEach(({ path, index }) => {
        const o = st.segments[index]?.opacity;
        if (o != null) restyle(path, { opacity: o }, `${o}`);
      });
      dotsRef.current.forEach(({ dot, index }) => {
        const v = st.vertices[index];
        if (v) restyle(dot, { fillOpacity: v.opacity, opacity: v.opacity }, `${v.opacity}`);
      });
      const setEl = (el, key, fn) => {
        if (!el || applied.get(el) === key) return;
        applied.set(el, key);
        fn(el);
      };
      pinsRef.current.forEach(({ marker, index }) => {
        const v = st.vertices[index];
        const el = marker.getElement();
        if (!v || !el) return;
        setEl(el, `${v.opacity}:${v.radius}`, () => {
          el.style.opacity = v.opacity;
          el.firstChild?.classList.toggle('hazard-disc--latest', v.radius === RADIUS.latest);
        });
      });
      let latest = -1;
      st.vertices.forEach((v, i) => { if (v.radius === RADIUS.latest) latest = i; });
      chipsRef.current.forEach(({ span, index }) => {
        const v = st.vertices[index];
        if (!v) return;
        setEl(span, `${v.opacity}:${index === latest}`, () => {
          span.style.opacity = v.opacity;
          span.classList.toggle('hazard-vertex-label--active', index === latest);
        });
      });
      windPathsRef.current.forEach((paths, idx) => {
        const w = st.wind[idx];
        if (!w) return;
        paths.forEach(({ fill, edge, band }) => {
          restyle(fill, { fillOpacity: w.fill[band] }, `${w.fill[band]}`);
          restyle(edge, { opacity: w.opacity, weight: w.weight }, `${w.opacity}:${w.weight}`);
        });
      });
      const activeIdx = st.wind.findIndex((w) => w.active);
      if (activeIdx !== activeSetRef.current) {
        activeSetRef.current = activeIdx;
        windPathsRef.current.get(activeIdx)?.forEach(({ edge }) => edge.bringToFront());
      }
      if (latest !== latestRef.current) {
        latestRef.current = latest;
        applyLabelVisibility();
      }
    }

    const marker = markerRef.current;
    if (marker && clockState) {
      const scrubbed = scrubRevision !== lastScrubRef.current;
      applyGlide(shouldGlide({
        isPlaying: isPlayingRef.current,
        reducedMotion: reducedMotion(),
        prevMs: lastClockMsRef.current,
        nextMs: clockMs,
        scrubbed,
      }));
      marker.setLatLng([clockState.position[1], toFrameRef.current(clockState.position[0])]);
      applySize(iconSizePx(parsed, clockMs, { isCurrent }) ?? sizeRef.current);
    }
    lastClockMsRef.current = clockMs ?? null;
    lastScrubRef.current = scrubRevision;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed, clockState, clockMs, isDark, labels, isCurrent, scrubRevision]);

  // Pressing pause or changing speed drops a pending glide immediately; the next normal tick re-enables it
  // at the new speed.
  useEffect(() => {
    applyGlide(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, playSpeed]);

  return null;
};

export default HazardGeometryLayer;
